import {
  VOUCHER_IMPORT_COLUMN_COUNT as EXPECTED_COLS
} from '../constants/voucherImportColumns.js';

function normalizeHeaderToken(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const LEGACY_HEADER_ALIASES = [
  new Set(['child_name', 'childname', 'name', 'student', 'student_name', 'child']),
  new Set(['case_number', 'casenumber', 'case_num', 'case']),
  new Set([
    'agreement_number',
    'agreementnumber',
    'agreement',
    'agreement_no',
    'agreementnum',
    'agrmt'
  ]),
  new Set(['eligibility_type', 'eligibilitytype', 'eligibility', 'type', 'elig']),
  new Set(['aa']),
  new Set(['sda']),
  new Set(['ada']),
  new Set(['cda']),
  new Set(['total', 'amount', 'received', 'sum', 'total_amount'])
];

/** NJ Provider Payments export columns, mapped onto the existing voucher fields. */
const NJ_FIELDS = {
  child_last_name: ['child_last_name'],
  child_first_name: ['child_first_name'],
  case_number: ['case_number', 'casenumber', 'case_num'],
  child_cin: ['child_cin', 'cin', 'childcin'],
  pmt_id: ['pmt_id', 'pmtid', 'payment_id', 'paymentid'],
  payment_type: ['payment_type', 'paymenttype'],
  authorized_amt: ['authorized_amt', 'authorized_amount', 'authorized'],
  fees: ['fees', 'fee'],
  levy: ['levy'],
  total_amt: ['total_amt', 'total_amount']
};

function cleanCell(value) {
  let s = String(value ?? '').trim();
  if (s.startsWith('=')) s = s.slice(1).trim();
  if (
    (s.startsWith('"') && s.endsWith('"') && s.length >= 2) ||
    (s.startsWith("'") && s.endsWith("'") && s.length >= 2)
  ) {
    s = s.slice(1, -1).trim();
  }
  return s;
}

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function parseDelimitedLine(line) {
  const tabCount = (line.match(/\t/g) || []).length;
  if (tabCount > 0) return line.split('\t').map((s) => s.trim());
  return parseCsvLine(line);
}

function legacyRowFromCells(parts) {
  return {
    child_name: parts[0] || '',
    case_number: parts[1] || '',
    agreement_number: parts[2] || '',
    eligibility_type: parts[3] || '',
    aa: parts[4] || '',
    sda: parts[5] || '',
    ada: parts[6] || '',
    cda: parts[7] || '',
    total: parts[8] || ''
  };
}

function isLegacyHeader(parts) {
  if (!parts || parts.length < EXPECTED_COLS) return false;
  const norm = parts.slice(0, EXPECTED_COLS).map((p) => normalizeHeaderToken(p));
  if (norm.some((c) => !c)) return false;
  return norm.every((cell, i) => LEGACY_HEADER_ALIASES[i].has(cell));
}

function indexNjHeaders(cells) {
  const norms = cells.map((c) => normalizeHeaderToken(c));
  const index = {};
  for (const [field, aliases] of Object.entries(NJ_FIELDS)) {
    const at = norms.findIndex((token) => aliases.includes(token));
    if (at >= 0) index[field] = at;
  }
  return index;
}

function isNjHeader(cells) {
  const index = indexNjHeaders(cells);
  return (
    index.child_last_name != null &&
    index.child_first_name != null &&
    (index.case_number != null || index.total_amt != null || index.pmt_id != null)
  );
}

function cellAt(cells, index, field) {
  const at = index[field];
  if (at == null || at >= cells.length) return '';
  return cleanCell(cells[at]);
}

/** LAST , FIRST — same shape already stored on voucher rows. */
function formatChildName(last, first) {
  const l = String(last ?? '').trim().toUpperCase();
  const f = String(first ?? '').trim().toUpperCase();
  if (l && f) return `${l} , ${f}`;
  return l || f;
}

function digitsOnly(value) {
  return String(value ?? '').replace(/\D/g, '');
}

function mapNjRow(cells, index) {
  const total = cellAt(cells, index, 'total_amt');
  return {
    child_name: formatChildName(
      cellAt(cells, index, 'child_last_name'),
      cellAt(cells, index, 'child_first_name')
    ),
    case_number: cellAt(cells, index, 'case_number'),
    cin: digitsOnly(cellAt(cells, index, 'child_cin')),
    agreement_number: cellAt(cells, index, 'pmt_id'),
    eligibility_type: cellAt(cells, index, 'payment_type'),
    aa: cellAt(cells, index, 'authorized_amt'),
    sda: cellAt(cells, index, 'fees'),
    ada: cellAt(cells, index, 'levy'),
    cda: total,
    total
  };
}

function rowIsBlank(row) {
  return Object.values(row).every((v) => !String(v ?? '').trim());
}

function parseNjRows(lines, headerCells) {
  const index = indexNjHeaders(headerCells);
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = lines[i].map(cleanCell);
    if (cells.every((c) => !c)) continue;
    const row = mapNjRow(cells, index);
    if (!rowIsBlank(row)) rows.push(row);
  }
  return rows.length
    ? { rows }
    : { error: 'No payment rows found under the NJ Provider Payments header.' };
}

/** "LAST , FIRST" is one column, but an unquoted comma splits it into two. */
function collapseLegacyColumns(parts) {
  if (parts.length === EXPECTED_COLS) return parts;
  if (parts.length < EXPECTED_COLS) return null;
  const extra = parts.length - EXPECTED_COLS;
  const name = parts.slice(0, extra + 1).join(' , ');
  return [name, ...parts.slice(extra + 1)];
}

function parseLegacyRows(cellRows) {
  let start = 0;
  if (cellRows.length && isLegacyHeader(cellRows[0])) start = 1;
  const rows = [];
  for (let i = start; i < cellRows.length; i += 1) {
    const parts = collapseLegacyColumns(cellRows[i].map(cleanCell));
    if (!parts) {
      return {
        error:
          'This is not the NJ Provider Payments export. Each legacy row needs 9 columns (tabs or commas).'
      };
    }
    if (parts.every((c) => !c)) continue;
    rows.push(legacyRowFromCells(parts));
  }
  return rows.length
    ? { rows }
    : { error: 'Paste at least one data row (after any header row).' };
}

function toCellRows(text) {
  return String(text ?? '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((l) => l.trim())
    .map((line) => parseDelimitedLine(line));
}

function looksLikeNjDataRow(cells) {
  if (!cells || cells.length < 14) return false;
  return cells.some((c) => /\d{1,2}\/\d{1,2}\/\d{4}\s*-\s*\d{1,2}\/\d{1,2}\/\d{4}/.test(String(c)));
}

export function parseVoucherImportText(text) {
  const cellRows = toCellRows(text);
  if (!cellRows.length) return { error: 'Paste at least one data row.' };
  const first = cellRows[0].map(cleanCell);
  if (isNjHeader(first)) return parseNjRows(cellRows, first);
  if (cellRows.some((row) => looksLikeNjDataRow(row.map(cleanCell)))) {
    return {
      error: 'Include the header row from the NJ Provider Payments export so the columns can be mapped.'
    };
  }
  return parseLegacyRows(cellRows);
}

export function parseVoucherImportMatrix(matrix) {
  const cellRows = [];
  for (const raw of matrix || []) {
    if (!Array.isArray(raw)) continue;
    const cells = raw.map((c) => cleanCell(c));
    if (cells.every((c) => !c)) continue;
    cellRows.push(cells);
  }
  if (!cellRows.length) return { error: 'Need at least one data row.' };
  if (isNjHeader(cellRows[0])) return parseNjRows(cellRows, cellRows[0]);
  if (cellRows.some((row) => looksLikeNjDataRow(row))) {
    return {
      error: 'Include the header row from the NJ Provider Payments export so the columns can be mapped.'
    };
  }
  return parseLegacyRows(cellRows);
}
